import React from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { IBuyboxResources } from '../../definition-extensions/buybox.ext.props.autogenerated';
import { RichTextComponent } from '@msdyn365-commerce/core';
import ConfirmationModal from './confirmation-modal';
import moment from 'moment';
import { PropertyType } from '../propertyTypeEnum';
import {
    IDateAndReasonForAppeal,
    IErrorSummary,
    IFormInput,
    IFormRadioSelection,
    ILCSABAgentInformation,
    ILCSABAppalentInformation,
    ILCSABCustomerInformation,
    ILCSABForm,
    ILCSABFormProps,
    ILCSABPersonCommonInfo,
    PERSON_INFO_FIELDS
} from '../types/buybox';
import addAppealToCartConfirmationAcknowledgement from './appeal-to-cart-confirmation-acknowledgement';

const ALL_PROVINCES = [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrad',
    'Nunavut',
    'Nova Scotia',
    'Ontario',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon'
] as const;

const ALL_LICENSE_TYPES = [
    '',
    'Alarm',
    'Bicycle Courier',
    'Body Rub',
    'Business',
    'Combative sports',
    'Concert',
    'Dating and Escort Service',
    'Downtown Pushcart Vendor',
    'Exotic Entertainers',
    'Extended Dance Event',
    'Livery Transport',
    'Massage',
    'Pet or livestock'
] as const;

const LCSABForm: React.FC<ILCSABFormProps> = props => {
    const {
        sdabCusInfoFirstName,
        sdabCusInfoLastName,
        sdabCusInfoEmail,
        sdabCusInfoEmailConf,
        sdabCusInfoAddress1,
        sdabCusInfoAddress2,
        sdabCusInfoCity,
        sdabCusInfoProvince,
        sdabCusInfoPPostalCode,
        requiredErrorSuffix,
        invalidErrorPrefix,
        matchErrorSuffix,
        appealReasonLengthError,
        privacyStatementHeader,
        reasonForAppealHeader,
        reasonForAppealInstruction,
        copyNoticeHeader,
        copyNoticeInstructions,
        formSubmissionAlert,
        nameAndReasonAlert,
        sameAsAppellantLabel,
        lcsabFormDesclaimerText
    } = props.restProps.resources;

    const appelantInformationInitialData: ILCSABAppalentInformation = {
        municipalAddress: {
            value: '',
            errorMessage: '',
            label: 'Municipal address of site',
            isRequired: true,
            section: 'municipal',
            name: 'municipalAddress',
            errorID: 'municipalAddressEr',
            inputWidth: 'col-sm-12',
            type: 'text'
        },
        firstName: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoFirstName,
            isRequired: true,
            section: 'appelantInfo',
            name: 'firstName',
            errorID: 'firstNameEr',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        lastName: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoLastName,
            isRequired: true,
            section: 'appelantInfo',
            name: 'lastName',
            errorID: 'lastNameEr',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        email: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoEmail,
            isRequired: true,
            section: 'appelantInfo',
            name: 'email',
            errorID: 'emailEr',
            inputWidth: 'col-sm-6',
            type: 'email'
        },
        emailConfirm: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoEmailConf,
            isRequired: true,
            section: 'appelantInfo',
            name: 'emailConfirm',
            errorID: 'emailConfirmEr',
            inputWidth: 'col-sm-6',
            type: 'email'
        },
        address1: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoAddress1,
            isRequired: true,
            section: 'appelantInfo',
            name: 'address1',
            errorID: 'address1Er',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        address2: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoAddress2,
            isRequired: false,
            section: 'appelantInfo',
            name: 'address2',
            errorID: 'address2Er',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        city: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoCity,
            isRequired: true,
            section: 'appelantInfo',
            name: 'city',
            errorID: 'cityEr',
            inputWidth: 'col-sm-4',
            type: 'text'
        },
        province: {
            value: 'Alberta',
            errorMessage: '',
            label: sdabCusInfoProvince,
            isRequired: true,
            section: 'appelantInfo',
            name: 'province',
            errorID: 'provinceEr',
            inputWidth: 'col-sm-4',
            type: 'select'
        },
        postalCode: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoPPostalCode,
            isRequired: true,
            section: 'appelantInfo',
            name: 'postalCode',
            errorID: 'postalCodeEr',
            inputWidth: 'col-sm-4',
            type: 'text',
            placeHolder: 'e.g. X9X 9X9'
        },
        dayTimePhoneNumber: {
            value: '',
            errorMessage: '',
            label: 'Daytime phone number',
            isRequired: true,
            section: 'appelantInfo',
            name: 'dayTimePhoneNumber',
            errorID: 'dayTimePhoneNumberEr',
            inputWidth: 'col-sm-6',
            type: 'text',
            placeHolder: 'e.g. 555-555-5555'
        },
        secondaryPhoneNumber: {
            value: '',
            errorMessage: '',
            label: 'Secondary phone number',
            isRequired: false,
            section: 'appelantInfo',
            name: 'secondaryPhoneNumber',
            errorID: 'secondaryPhoneNumberEr',
            inputWidth: 'col-sm-6',
            type: 'text',
            placeHolder: 'e.g. 555-555-5555'
        }
    };
    const customerInformationInitialData: ILCSABCustomerInformation = {
        firstName: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoFirstName,
            isRequired: true,
            section: 'customerInfo',
            name: 'firstName',
            errorID: 'firstNameEr',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        lastName: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoLastName,
            isRequired: true,
            section: 'customerInfo',
            name: 'lastName',
            errorID: 'lastNameEr',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        email: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoEmail,
            isRequired: true,
            section: 'customerInfo',
            name: 'email',
            errorID: 'emailEr',
            inputWidth: 'col-sm-6',
            type: 'email'
        },
        emailConfirm: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoEmailConf,
            isRequired: true,
            section: 'customerInfo',
            name: 'emailConfirm',
            errorID: 'emailConfirmEr',
            inputWidth: 'col-sm-6',
            type: 'email'
        },
        address1: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoAddress1,
            isRequired: true,
            section: 'customerInfo',
            name: 'address1',
            errorID: 'address1Er',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        address2: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoAddress2,
            isRequired: false,
            section: 'customerInfo',
            name: 'address2',
            errorID: 'address2Er',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        city: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoCity,
            isRequired: true,
            section: 'customerInfo',
            name: 'city',
            errorID: 'cityEr',
            inputWidth: 'col-sm-4',
            type: 'text'
        },
        province: {
            value: 'Alberta',
            errorMessage: '',
            label: sdabCusInfoProvince,
            isRequired: true,
            section: 'customerInfo',
            name: 'province',
            errorID: 'provinceEr',
            inputWidth: 'col-sm-4',
            type: 'select'
        },
        postalCode: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoPPostalCode,
            isRequired: true,
            section: 'customerInfo',
            name: 'postalCode',
            errorID: 'postalCodeEr',
            inputWidth: 'col-sm-4',
            type: 'text',
            placeHolder: 'e.g. X9X 9X9'
        },
        dayTimePhoneNumber: {
            value: '',
            errorMessage: '',
            label: 'Daytime phone number',
            isRequired: true,
            section: 'customerInfo',
            name: 'dayTimePhoneNumber',
            errorID: 'dayTimePhoneNumberEr',
            inputWidth: 'col-sm-6',
            type: 'text',
            placeHolder: 'e.g. 555-555-5555'
        },
        secondaryPhoneNumber: {
            value: '',
            errorMessage: '',
            label: 'Secondary phone number',
            isRequired: false,
            section: 'customerInfo',
            name: 'secondaryPhoneNumber',
            errorID: 'secondaryPhoneNumberEr',
            inputWidth: 'col-sm-6',
            type: 'text',
            placeHolder: 'e.g. 555-555-5555'
        }
    };
    const agentInformationInitialData: ILCSABAgentInformation = {
        agentName: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoLastName,
            isRequired: true,
            section: 'agentInfo',
            name: 'agentName',
            errorID: 'agentNameEr',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        email: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoEmail,
            isRequired: true,
            section: 'agentInfo',
            name: 'email',
            errorID: 'emailEr',
            inputWidth: 'col-sm-6',
            type: 'email'
        },
        emailConfirm: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoEmailConf,
            isRequired: true,
            section: 'agentInfo',
            name: 'emailConfirm',
            errorID: 'emailConfirmEr',
            inputWidth: 'col-sm-6',
            type: 'email'
        },
        address1: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoAddress1,
            isRequired: true,
            section: 'agentInfo',
            name: 'address1',
            errorID: 'address1Er',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        address2: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoAddress2,
            isRequired: false,
            section: 'agentInfo',
            name: 'address2',
            errorID: 'address2Er',
            inputWidth: 'col-sm-6',
            type: 'text'
        },
        city: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoCity,
            isRequired: true,
            section: 'agentInfo',
            name: 'city',
            errorID: 'cityEr',
            inputWidth: 'col-sm-4',
            type: 'text'
        },
        province: {
            value: 'Alberta',
            errorMessage: '',
            label: sdabCusInfoProvince,
            isRequired: true,
            section: 'agentInfo',
            name: 'province',
            errorID: 'provinceEr',
            inputWidth: 'col-sm-4',
            type: 'select'
        },
        postalCode: {
            value: '',
            errorMessage: '',
            label: sdabCusInfoPPostalCode,
            isRequired: true,
            section: 'agentInfo',
            name: 'postalCode',
            errorID: 'postalCodeEr',
            inputWidth: 'col-sm-4',
            type: 'text',
            placeHolder: 'e.g. X9X 9X9'
        },
        dayTimePhoneNumber: {
            value: '',
            errorMessage: '',
            label: 'Daytime phone number',
            isRequired: true,
            section: 'agentInfo',
            name: 'dayTimePhoneNumber',
            errorID: 'dayTimePhoneNumberEr',
            inputWidth: 'col-sm-6',
            type: 'text',
            placeHolder: 'e.g. 555-555-5555'
        },
        secondaryPhoneNumber: {
            value: '',
            errorMessage: '',
            label: 'Secondary phone number',
            isRequired: false,
            section: 'agentInfo',
            name: 'secondaryPhoneNumber',
            errorID: 'secondaryPhoneNumberEr',
            inputWidth: 'col-sm-6',
            type: 'text',
            placeHolder: 'e.g. 555-555-5555'
        }
    };
    const dateAndReasonForAppealInitialData: IDateAndReasonForAppeal = {
        date: { value: '', type: 'date', name: 'date', label: 'Date', section: 'date', errorMessage: '', isRequired: true, inputWidth: '' },
        reasonForAppeal: {
            value: '',
            errorMessage: '',
            label: 'Reasons for appeal',
            isRequired: true,
            section: 'reasonForAppeal',
            name: 'reasonForAppeal',
            errorID: 'reasonForAppealEr',
            inputWidth: '',
            type: ''
        }
    };
    const usingAgentSelectionInitialData: IFormRadioSelection = {
        value: '',
        errorMessage: '',
        label: 'Agent Information',
        section: 'usingAgent'
    };
    const appealTypeInitialData: IFormRadioSelection = {
        value: '',
        errorMessage: '',
        label: 'Appeal Type',
        section: 'appealType'
    };
    const petAppealTypeInitialData: IFormRadioSelection = {
        value: '',
        errorMessage: '',
        label: 'Pet Ownership',
        section: 'petOwnership'
    };
    const remedialTypeInitialData: IFormRadioSelection = {
        value: '',
        errorMessage: '',
        label: 'Remedial Order',
        section: 'remedialOrder'
    };
    const licenseTypeInitialData = {
        value: '',
        errorMessage: '',
        label: 'License/permit',
        section: 'licensePermit'
    };
    const typeOfLicenseInitialData = {
        value: '',
        errorMessage: '',
        label: 'Type of license',
        section: 'licenseType'
    };
    const [isDisabled, setIsDisabled] = React.useState(false);
    const [customerInfo, setCustomerInfo] = React.useState<ILCSABCustomerInformation>(customerInformationInitialData);
    const [appelantInformation, setAppelantInformation] = React.useState<ILCSABAppalentInformation>(appelantInformationInitialData);
    const [agentInformation, setAgentInformation] = React.useState<ILCSABAgentInformation>(agentInformationInitialData);
    const [usingAgent, setUsingAgent] = React.useState<IFormRadioSelection>(usingAgentSelectionInitialData);
    const [appealType, setAppealType] = React.useState<IFormRadioSelection>(appealTypeInitialData);
    const [petAppealType, setPetAppealType] = React.useState<IFormRadioSelection>(petAppealTypeInitialData);
    const [remedialType, setRemedialType] = React.useState<IFormRadioSelection>(remedialTypeInitialData);
    const [licenseType, setLicenseType] = React.useState<IFormRadioSelection>(licenseTypeInitialData);
    const [typeOfLicense, setTypeOfLicense] = React.useState<IFormRadioSelection>(typeOfLicenseInitialData);
    const [dateAndReasonForAppeal, setDateAndReasonForAppeal] = React.useState<IDateAndReasonForAppeal>(dateAndReasonForAppealInitialData);
    const [isUseAppelantInfoChecked, setIsUseAppelantInfoChecked] = React.useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState(false);
    const { isModelOpen, toggleSDABModal, restProps, product } = props;

    React.useEffect(() => {
        if (isUseAppelantInfoChecked) {
            const newInfo = { ...appelantInformation };
            for (const key in newInfo) {
                newInfo[key] = { ...newInfo[key], errorMessage: '' };
            }
            setCustomerInfo(prevState => {
                return {
                    ...prevState,
                    firstName: { ...newInfo.firstName },
                    lastName: { ...newInfo.lastName },
                    email: { ...newInfo.email },
                    emailConfirm: { ...newInfo.emailConfirm },
                    address1: { ...newInfo.address1 },
                    address2: { ...newInfo.address2 },
                    city: { ...newInfo.city },
                    province: { ...newInfo.province },
                    postalCode: { ...newInfo.postalCode },
                    dayTimePhoneNumber: { ...newInfo.dayTimePhoneNumber },
                    secondaryPhoneNumber: { ...newInfo.secondaryPhoneNumber }
                };
            });
        }
    }, [isUseAppelantInfoChecked, appelantInformation]);

    const handleAppealType = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAppealType({ ...appealType, value: e.target.value, errorMessage: '' });
        setPetAppealType(petAppealTypeInitialData);
        setRemedialType(remedialTypeInitialData);
        setTypeOfLicense(typeOfLicenseInitialData);
    };
    const handlePetAppealType = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPetAppealType({ ...petAppealType, value: e.target.value, errorMessage: '' });
        setRemedialType(remedialTypeInitialData);
        setTypeOfLicense(typeOfLicenseInitialData);
        setLicenseType(licenseTypeInitialData);
    };
    const handleRemedialType = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRemedialType({ ...remedialType, value: e.target.value, errorMessage: '' });
        setPetAppealType(petAppealTypeInitialData);
        setTypeOfLicense(typeOfLicenseInitialData);
        setLicenseType(licenseTypeInitialData);
    };
    const handleLicenseType = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLicenseType({ ...licenseType, errorMessage: '', value: e.target.value });
        setPetAppealType(petAppealTypeInitialData);
        setRemedialType(remedialTypeInitialData);
    };
    const handleTypeOfLicenseSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTypeOfLicense({ ...typeOfLicense, value: e.target.value, errorMessage: '' });
        setPetAppealType(petAppealTypeInitialData);
        setRemedialType(remedialTypeInitialData);
    };
    const handleDateAndReasonForAppeal = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const name = e.target.name as keyof IDateAndReasonForAppeal;
        setDateAndReasonForAppeal(prevState => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label}${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: '' }
                };
            }
        });
    };
    const handleCustomerInfo = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const name = e.target.name as keyof ILCSABCustomerInformation;
        setCustomerInfo(prevState => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                // display error message
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label}${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: '' }
                };
            }
        });
    };
    const handleAgentInformation = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const name = e.target.name as keyof ILCSABAgentInformation;
        setAgentInformation(prevState => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                // display error message
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label}${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: '' }
                };
            }
        });
    };
    const handleAppelantInformation = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        const name = e.target.name as keyof ILCSABAppalentInformation;
        setAppelantInformation(prevState => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                // update error message
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label}${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: '' }
                };
            }
        });
    };
    const handleUsingAgent = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setUsingAgent({ ...usingAgent, value, errorMessage: '' });
        if (value.toLowerCase() !== 'yes') {
            setAgentInformation(agentInformationInitialData);
        }
    };
    const handleIsUseCustomerInfoChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setIsUseAppelantInfoChecked(checked);
    };

    const handleFormValidation = async () => {
        //1- Clear previous error message
        //2- Check all required fields are filled for all sections
        setIsDisabled(true);
        let hasError = false;
        for (const [key, property] of Object.entries(appelantInformation)) {
            if (!property.value && property.value.length <= 0 && appelantInformation[key].isRequired) {
                hasError = true;
                setAppelantInformation(prevState => {
                    return {
                        ...prevState,
                        [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                    };
                });
            }
        }
        for (const [key, property] of Object.entries(dateAndReasonForAppeal)) {
            if (!property.value && property.value.length <= 0 && dateAndReasonForAppeal[key].isRequired) {
                hasError = true;
                setDateAndReasonForAppeal(prevState => {
                    return {
                        ...prevState,
                        [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                    };
                });
            }
        }
        // User didn't select the any option show the error message
        if (usingAgent.value.length <= 0) {
            hasError = true;
            setUsingAgent({ ...usingAgent, errorMessage: `${usingAgent.label}${requiredErrorSuffix}` });
        }
        if (usingAgent.value.length > 0 && usingAgent.value.toLowerCase() === 'yes') {
            for (const [key, property] of Object.entries(agentInformation)) {
                if (!property.value && property.value.length <= 0 && agentInformation[key].isRequired) {
                    hasError = true;
                    setAgentInformation(prevState => {
                        return {
                            ...prevState,
                            [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                        };
                    });
                }
            }
            if (agentInformation.dayTimePhoneNumber && agentInformation.dayTimePhoneNumber.value.length > 0) {
                if (!validatePhoneNumber(agentInformation.dayTimePhoneNumber.value)) {
                    hasError = true;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        dayTimePhoneNumber: {
                            ...prevSt.dayTimePhoneNumber,
                            errorMessage: `${invalidErrorPrefix}${prevSt.dayTimePhoneNumber.label}.`
                        }
                    }));
                }
            }
            if (agentInformation.secondaryPhoneNumber && agentInformation.secondaryPhoneNumber.value.length > 0) {
                if (!validatePhoneNumber(agentInformation.secondaryPhoneNumber.value)) {
                    hasError = true;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        secondaryPhoneNumber: {
                            ...prevSt.secondaryPhoneNumber,
                            errorMessage: `${invalidErrorPrefix}${prevSt.secondaryPhoneNumber.label}.`
                        }
                    }));
                }
            }
            // Validate the email address & matching for customerInfo and appelantInfo sections
            //validate the email address (customerInfoSection) and store it's result
            let isValidEmailAddressAgentInformaiton;
            let isValidConfirmEmailAddressAgentInformation;

            if (agentInformation.email.value.length > 0) {
                if (!validateEmail(agentInformation.email.value)) {
                    hasError = true;
                    isValidEmailAddressAgentInformaiton = false;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                    }));
                } else {
                    isValidEmailAddressAgentInformaiton = true;
                    setAgentInformation(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
                }
            }

            if (agentInformation.emailConfirm.value.length > 0) {
                if (!validateEmail(agentInformation.emailConfirm.value)) {
                    hasError = true;
                    isValidConfirmEmailAddressAgentInformation = false;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                    }));
                } else {
                    isValidConfirmEmailAddressAgentInformation = true;
                    setAgentInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                }
            }
            // After validating the EmailAddress now Match the email & emailConfirm and show the error message under emailConfirm address section
            if (
                agentInformation.email.value.length > 0 &&
                agentInformation.emailConfirm.value.length > 0 &&
                isValidEmailAddressAgentInformaiton &&
                isValidConfirmEmailAddressAgentInformation
            ) {
                if (agentInformation.email.value.toLowerCase() !== agentInformation.emailConfirm.value.toLowerCase()) {
                    hasError = true;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${prevSt.emailConfirm.label}${matchErrorSuffix}` }
                    }));
                } else {
                    setAgentInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                }
            } else {
                // if address is valid then don't display the error for email section
                if (agentInformation.email.value.length > 0 && isValidEmailAddressAgentInformaiton) {
                    setAgentInformation(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
                } else {
                    hasError = true;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                    }));
                }

                if (agentInformation.emailConfirm.value.length > 0 && isValidConfirmEmailAddressAgentInformation) {
                    setAgentInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                } else {
                    hasError = true;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                    }));
                }
            }
            // validation of postal code for (agentInformation)
            if (agentInformation.postalCode && agentInformation.postalCode.value.length > 0) {
                if (!checkCanadianPostalCode(agentInformation.postalCode.value)) {
                    hasError = true;
                    setAgentInformation(prevSt => ({
                        ...prevSt,
                        postalCode: { ...prevSt.postalCode, errorMessage: `${invalidErrorPrefix}${prevSt.postalCode.label}` }
                    }));
                }
            }
            /** *********************************************************************** */
            /** *********************************************************************** */
        }
        if (appealType.value.length <= 0) {
            hasError = true;
            setAppealType({ ...appealType, errorMessage: `${appealType.label}${requiredErrorSuffix}` });
        }
        if (appealType.value === AppealType.PetOwnershipAppeals && petAppealType.value.length <= 0) {
            setPetAppealType({ ...petAppealType, errorMessage: `${petAppealType.label}${requiredErrorSuffix}` });
        }
        if (appealType.value === AppealType.RemedialOrder && remedialType.value.length <= 0) {
            setRemedialType({ ...remedialType, errorMessage: `${remedialType.label}${requiredErrorSuffix}` });
        }
        if (appealType.value === AppealType.LicensePermitDecisionOrNotice && licenseType.value.length <= 0) {
            setLicenseType({ ...licenseType, errorMessage: `${licenseType.label}${requiredErrorSuffix}` });
        }
        if (appealType.value === AppealType.LicensePermitDecisionOrNotice && typeOfLicense.value.length <= 0) {
            setTypeOfLicense({ ...typeOfLicense, errorMessage: `${typeOfLicense.label}${requiredErrorSuffix}` });
        }
        const {
            value: dateValue,
            value: { length: dateLength }
        } = dateAndReasonForAppeal.date;
        if (dateLength <= 0) {
            hasError = true;
            setDateAndReasonForAppeal(prevSt => ({
                ...prevSt,
                date: {
                    ...prevSt.date,
                    errorMessage: `${dateAndReasonForAppeal.date.label}${requiredErrorSuffix}`
                }
            }));
        }
        if (dateLength > 0) {
            const formattedDate = moment(dateValue, 'YYYY-MM-DD', true);
            const year = formattedDate.year();
            if (!formattedDate.isValid() || year.toString().length !== 4) {
                hasError = true;
                setDateAndReasonForAppeal(prevSt => ({
                    ...prevSt,
                    date: {
                        ...prevSt.date,
                        errorMessage: `${invalidErrorPrefix}${dateAndReasonForAppeal.date.label}`
                    }
                }));
            }
        }
        if (dateAndReasonForAppeal.reasonForAppeal.value.length > 2000) {
            hasError = true;
            setDateAndReasonForAppeal(prevSt => {
                return {
                    ...prevSt,
                    reasonForAppeal: {
                        ...prevSt.reasonForAppeal,
                        errorMessage: `${appealReasonLengthError}`
                    }
                };
            });
        }

        if (appelantInformation.secondaryPhoneNumber && appelantInformation.secondaryPhoneNumber.value.length > 0) {
            if (!validatePhoneNumber(appelantInformation.secondaryPhoneNumber.value)) {
                hasError = true;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    secondaryPhoneNumber: {
                        ...prevSt.secondaryPhoneNumber,
                        errorMessage: `${invalidErrorPrefix}${prevSt.secondaryPhoneNumber.label}.`
                    }
                }));
            }
        }

        if (appelantInformation.dayTimePhoneNumber && appelantInformation.dayTimePhoneNumber.value.length > 0) {
            if (!validatePhoneNumber(appelantInformation.dayTimePhoneNumber.value)) {
                hasError = true;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    dayTimePhoneNumber: {
                        ...prevSt.dayTimePhoneNumber,
                        errorMessage: `${invalidErrorPrefix}${prevSt.dayTimePhoneNumber.label}.`
                    }
                }));
            }
        }
        if (appelantInformation.postalCode && appelantInformation.postalCode.value.length > 0) {
            if (!checkCanadianPostalCode(appelantInformation.postalCode.value)) {
                hasError = true;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    postalCode: { ...prevSt.postalCode, errorMessage: `${invalidErrorPrefix}${prevSt.postalCode.label}` }
                }));
            }
        }
        // Same validation for email-address for appelantInformation Section

        // Validate the email address & matching for customerInfo and appelantInfo sections
        //validate the email address (customerInfoSection) and store it's result
        let isValidEmailAddressAppelantInfoSection;
        let isValidConfirmEmailAddressAppelantInfoSection;

        if (appelantInformation.email.value.length > 0) {
            if (!validateEmail(appelantInformation.email.value)) {
                hasError = true;
                isValidEmailAddressAppelantInfoSection = false;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                }));
            } else {
                isValidEmailAddressAppelantInfoSection = true;
                setAppelantInformation(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
            }
        }

        if (appelantInformation.emailConfirm.value.length > 0) {
            if (!validateEmail(appelantInformation.emailConfirm.value)) {
                hasError = true;
                isValidConfirmEmailAddressAppelantInfoSection = false;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                }));
            } else {
                isValidConfirmEmailAddressAppelantInfoSection = true;
                setAppelantInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
            }
        }
        // After validating the EmailAddress now Match the email & emailConfirm and show the error message under emailConfirm address section
        if (
            appelantInformation.email.value.length > 0 &&
            appelantInformation.emailConfirm.value.length > 0 &&
            isValidEmailAddressAppelantInfoSection &&
            isValidConfirmEmailAddressAppelantInfoSection
        ) {
            if (appelantInformation.email.value.toLowerCase() !== appelantInformation.emailConfirm.value.toLowerCase()) {
                hasError = true;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${prevSt.emailConfirm.label}${matchErrorSuffix}` }
                }));
            } else {
                setAppelantInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
            }
        } else {
            // if address is valid then don't display the error for email section
            if (appelantInformation.email.value.length > 0 && isValidEmailAddressAppelantInfoSection) {
                setAppelantInformation(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
            } else {
                hasError = true;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                }));
            }

            if (appelantInformation.emailConfirm.value.length > 0 && isValidConfirmEmailAddressAppelantInfoSection) {
                setAppelantInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
            } else {
                hasError = true;
                setAppelantInformation(prevSt => ({
                    ...prevSt,
                    emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                }));
            }
        }
        if (!isUseAppelantInfoChecked) {
            for (const [key, property] of Object.entries(customerInfo)) {
                if (!property.value && property.value.length <= 0 && customerInfo[key].isRequired) {
                    hasError = true;
                    setCustomerInfo(prevState => {
                        return {
                            ...prevState,
                            [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                        };
                    });
                }
            }
            // validation of residentialPhoneNumber for (customerInfo and appelantInformation sections)
            if (customerInfo.dayTimePhoneNumber && customerInfo.dayTimePhoneNumber.value.length > 0) {
                if (!validatePhoneNumber(customerInfo.dayTimePhoneNumber.value)) {
                    hasError = true;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        dayTimePhoneNumber: {
                            ...prevSt.dayTimePhoneNumber,
                            errorMessage: `${invalidErrorPrefix}${prevSt.dayTimePhoneNumber.label}.`
                        }
                    }));
                }
            }
            if (customerInfo.secondaryPhoneNumber && customerInfo.secondaryPhoneNumber.value.length > 0) {
                if (!validatePhoneNumber(customerInfo.secondaryPhoneNumber.value)) {
                    hasError = true;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        secondaryPhoneNumber: {
                            ...prevSt.secondaryPhoneNumber,
                            errorMessage: `${invalidErrorPrefix}${prevSt.secondaryPhoneNumber.label}.`
                        }
                    }));
                }
            }
            // validation of postal code for (customerInfo and appelantInformation sections)
            if (customerInfo.postalCode && customerInfo.postalCode.value.length > 0) {
                if (!checkCanadianPostalCode(customerInfo.postalCode.value)) {
                    hasError = true;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        postalCode: { ...prevSt.postalCode, errorMessage: `${invalidErrorPrefix}${prevSt.postalCode.label}` }
                    }));
                }
            }
            // Validate the email address & matching for customerInfo and appelantInfo sections
            //validate the email address (customerInfoSection) and store it's result
            let isValidEmailAddressCustomerInfoSection;
            let isValidConfirmEmailAddressCustomerInfoSection;
            if (customerInfo.email.value.length > 0) {
                if (!validateEmail(customerInfo.email.value)) {
                    hasError = true;
                    isValidEmailAddressCustomerInfoSection = false;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                    }));
                } else {
                    isValidEmailAddressCustomerInfoSection = true;
                    setCustomerInfo(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
                }
            }
            if (customerInfo.emailConfirm.value.length > 0) {
                if (!validateEmail(customerInfo.emailConfirm.value)) {
                    hasError = true;
                    isValidConfirmEmailAddressCustomerInfoSection = false;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                    }));
                } else {
                    isValidConfirmEmailAddressCustomerInfoSection = true;
                    setCustomerInfo(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                }
            }
            // After validating the EmailAddress now Match the email & emailConfirm and show the error message under emailConfirm address section
            if (
                customerInfo.email.value.length > 0 &&
                customerInfo.emailConfirm.value.length > 0 &&
                isValidConfirmEmailAddressCustomerInfoSection &&
                isValidEmailAddressCustomerInfoSection
            ) {
                if (customerInfo.email.value.toLowerCase() !== customerInfo.emailConfirm.value.toLowerCase()) {
                    hasError = true;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${prevSt.emailConfirm.label}${matchErrorSuffix}` }
                    }));
                } else {
                    setCustomerInfo(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                }
            } else {
                // if address is valid then don't display the error for email section
                if (customerInfo.email.value.length > 0 && isValidEmailAddressCustomerInfoSection) {
                    setCustomerInfo(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
                } else {
                    hasError = true;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                    }));
                }

                if (customerInfo.emailConfirm.value.length > 0 && isValidConfirmEmailAddressCustomerInfoSection) {
                    setCustomerInfo(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                } else {
                    hasError = true;
                    setCustomerInfo(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                    }));
                }
            }
        }
        // If we dont' have any errors proceed with add product to cart and attributes
        if (!hasError) {
            // update the AllAddress (Parent Hook coming from buybox.view.tsx)
            const lcsabData: ILCSABForm = {
                customerInformation: customerInfo,
                appellantInformation: appelantInformation,
                usingAgent: usingAgent.value,
                agentInformation: agentInformation,
                dateDecisionAndReasonForAppeal: dateAndReasonForAppeal,
                categoryAppealType: appealType.value,
                petAppealType: petAppealType.value,
                remedialType: remedialType.value,
                licenseType: licenseType.value,
                typeOfLicense: typeOfLicense.value,
                isChecked: true
            };
            // Add product to cart.
            await props.addProductToCartWithAttribute(
                [
                    {
                        addressVerified: {},
                        serviceAddress: {},
                        sdab: {},
                        lcsab: lcsabData,
                        legalDescriptionCondoVerified: {},
                        lookForVerified: {},
                        legalDescriptionVerified: {},
                        propertyType: PropertyType.lcsab,
                        relatedProduct: [product]
                    }
                ],
                restProps.context
            );
            setIsDisabled(false);
            // Clear hooks/state to initial form
            setCustomerInfo(customerInformationInitialData);
            setAppelantInformation(appelantInformationInitialData);
            setUsingAgent(usingAgentSelectionInitialData);
            setAgentInformation(agentInformationInitialData);
            setAppealType(appealTypeInitialData);
            setRemedialType(remedialTypeInitialData);
            setPetAppealType(petAppealTypeInitialData);
            setLicenseType(licenseTypeInitialData);
            setTypeOfLicense(typeOfLicenseInitialData);
            toggleSDABModal(false);
        } else {
            setIsDisabled(false);
        }
        // Need to check for email & email-confirm match update error message
        // Need to check for appeal reason required should be less than 2000 characters update error message
        // Need to check for postal code, residential phone validation update error message
    };
    const validateEmail = (email: string) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };
    const checkCanadianPostalCode = (postalCode: string) => {
        const postalCodeRegex = new RegExp(/^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVXY][ -]?\d[ABCEGHJKLMNPRSTVXY]\d$/i);

        return postalCodeRegex.test(postalCode);
    };
    const validatePhoneNumber = (phoneNumber: string) => {
        let phoneNumberRegex = /^\d\d\d-\d\d\d-\d\d\d\d$/i;
        return phoneNumberRegex.test(phoneNumber);
    };

    const renderErrorSummary = () => {
        const errors: IErrorSummary[] = [];
        for (const [_, property] of Object.entries(appelantInformation)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        for (const [_, property] of Object.entries(customerInfo)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        for (const [_, property] of Object.entries(agentInformation)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        if (usingAgent.value.length <= 0 && usingAgent.errorMessage) {
            errors.push({ errorMessage: usingAgent.errorMessage, section: usingAgent.section });
        }
        if (appealType.value.length <= 0 && appealType.errorMessage) {
            errors.push({ errorMessage: appealType.errorMessage, section: appealType.section });
        }
        if (appealType.value === AppealType.PetOwnershipAppeals && petAppealType.value.length <= 0 && petAppealType.errorMessage) {
            errors.push({ errorMessage: petAppealType.errorMessage, section: petAppealType.section });
        }
        if (appealType.value === AppealType.RemedialOrder && remedialType.value.length <= 0 && remedialType.errorMessage) {
            errors.push({ errorMessage: remedialType.errorMessage, section: remedialType.section });
        }
        if (appealType.value === AppealType.LicensePermitDecisionOrNotice && licenseType.value.length <= 0 && licenseType.errorMessage) {
            errors.push({ errorMessage: licenseType.errorMessage, section: licenseType.section });
        }
        if (
            appealType.value === AppealType.LicensePermitDecisionOrNotice &&
            typeOfLicense.value.length <= 0 &&
            typeOfLicense.errorMessage
        ) {
            errors.push({ errorMessage: typeOfLicense.errorMessage, section: typeOfLicense.section });
        }
        for (const [_, property] of Object.entries(dateAndReasonForAppeal)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        return errors.map(error => {
            return (
                <li>
                    <a href={`#${error.section}`}>{error.errorMessage}</a>
                </li>
            );
        });
    };
    const closeSDABModal = () => {
        setIsConfirmationModalOpen(true);
    };
    return (
        <Modal
            role='document'
            aria-labelledby='productSearchTitle'
            className='cui modal-xl'
            isOpen={isModelOpen}
            toggle={closeSDABModal}
            keyboard={true}
        >
            <div className='modal-header'>
                <h2 id='productSearchTitle' tabIndex={0} className='modal-title'>
                    {restProps.resources.lcsabFormHeader}
                </h2>
                <button className='btn-modal-close cui btn-md primary-text' onClick={closeSDABModal}>
                    {restProps.resources.closeModalLabel}
                </button>
            </div>
            <ModalBody>
                <div className='property-search-wrap'>
                    <ConfirmationModal
                        isModalOpen={isConfirmationModalOpen}
                        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                        toggleSDABModal={toggleSDABModal}
                        restProps={props.restProps}
                    />
                    <h3>{privacyStatementHeader}</h3>
                    <p className='b-border-box'>
                        <RichTextComponent text={lcsabFormDesclaimerText} />
                    </p>
                    <h3>{reasonForAppealHeader}</h3>
                    <p className='b-border-box'>
                        <RichTextComponent text={reasonForAppealInstruction} />
                    </p>
                    <h3>{copyNoticeHeader}</h3>
                    <p className='b-border-box'>
                        <RichTextComponent text={copyNoticeInstructions} />
                    </p>
                    <div className='cui alert-banner inline caution'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-triangle'></span>
                            <p>
                                <RichTextComponent text={formSubmissionAlert} />
                            </p>
                        </div>
                    </div>

                    <form className='form-wrap' id='municipal'>
                        <div className='form-group'>
                            <LabelledInput
                                field={appelantInformation.municipalAddress}
                                resources={restProps.resources}
                                onChangeHandler={handleAppelantInformation}
                            />
                        </div>
                    </form>
                    <h3 id='appelantInfo'>{restProps.resources.sdabAppellantInfoLabel}</h3>
                    <div className='cui alert-banner inline caution'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-triangle'></span>
                            <RichTextComponent text={nameAndReasonAlert} />
                        </div>
                    </div>
                    <form className='form-wrap' id='appelantInfo'>
                        <div className='form-group'>
                            <div className='row'>
                                <LabelledInput
                                    field={appelantInformation.firstName}
                                    onChangeHandler={handleAppelantInformation}
                                    resources={restProps.resources}
                                />
                                <LabelledInput
                                    field={appelantInformation.lastName}
                                    onChangeHandler={handleAppelantInformation}
                                    resources={restProps.resources}
                                />
                                {renderPersonInfoInputs(appelantInformation, handleAppelantInformation, restProps.resources)}
                            </div>
                        </div>
                    </form>
                    <h3 id='customerInfo'>{restProps.resources.sdabCustomerInfoLabel}</h3>
                    <label htmlFor='useAppelantInformation' className='form-wrap conf-box'>
                        <input
                            type='checkbox'
                            id='useAppelantInformation'
                            name='useAppelantInformation'
                            onChange={handleIsUseCustomerInfoChecked}
                        />
                        {sameAsAppellantLabel}
                    </label>
                    {/* Hide form when checkbox is checked */}
                    {!isUseAppelantInfoChecked && (
                        <form className='form-wrap'>
                            <div className='form-group'>
                                <div className='row'>
                                    <LabelledInput
                                        field={customerInfo.firstName}
                                        onChangeHandler={handleCustomerInfo}
                                        resources={restProps.resources}
                                    />
                                    <LabelledInput
                                        field={customerInfo.lastName}
                                        onChangeHandler={handleCustomerInfo}
                                        resources={restProps.resources}
                                    />
                                    {renderPersonInfoInputs(customerInfo, handleCustomerInfo, restProps.resources)}
                                </div>
                            </div>
                        </form>
                    )}
                    <AgentInformationSection
                        resources={restProps.resources}
                        agentInformation={agentInformation}
                        agentInformationHandler={handleAgentInformation}
                        usingAgent={usingAgent}
                        usingAgentHandler={handleUsingAgent}
                    />
                    <AppealTypeSection
                        resources={restProps.resources}
                        appealType={appealType}
                        appealTypeHandler={handleAppealType}
                        petAppealType={petAppealType}
                        petAppealTypeHandler={handlePetAppealType}
                        remedialType={remedialType}
                        remedialTypeHandler={handleRemedialType}
                        licenseType={licenseType}
                        licenseTypeHandler={handleLicenseType}
                        typeOfLicense={typeOfLicense}
                        typeOfLicenseHandler={handleTypeOfLicenseSelection}
                        dateAndReasonForAppeal={dateAndReasonForAppeal}
                        dateAndReasonForAppealHandler={handleDateAndReasonForAppeal}
                    />
                    <div className='error-Summary'>{renderErrorSummary()}</div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className='btn-box'>
                    <button onClick={handleFormValidation} type='button' className='cui btn-md primary'>
                        <span className='btn-wrapper'>
                            <span className='btn-text'>{restProps.resources.addAppealToCartButtonLabel}</span>
                            <span className='cicon-angle-right right'></span>
                        </span>
                    </button>
                </div>
                {isDisabled ? (
                    <div className='spinner-wrap'>
                        <div className='preload-spinner-container' role='status'>
                            <div className='preload-spinner'></div>
                            <p>{props.restProps.resources.loadingLabel}</p>
                        </div>
                    </div>
                ) : null}
            </ModalFooter>
        </Modal>
    );
};

const renderProvinceOptions = () => {
    return ALL_PROVINCES.map(province => (
        <option key={province} value={province}>
            {province}
        </option>
    ));
};
const renderPersonInfoInputs = (
    info: ILCSABPersonCommonInfo,
    onChange: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void,
    resources: IBuyboxResources
) =>
    PERSON_INFO_FIELDS.map(field =>
        field === 'province' ? (
            <div className='col-sm-4'>
                <label htmlFor='province' className='req'>
                    {resources.sdabCusInfoProvince}
                    <span className='starick'>{resources.requiredText}</span>
                </label>
                <select
                    className={`form-field ${info.province.errorMessage && 'error'}`}
                    value={info.province.value}
                    name='province'
                    id='province'
                    aria-describedby='provinceEr'
                    onChange={onChange}
                >
                    {renderProvinceOptions()}
                </select>
                {info.province.errorMessage && (
                    <div id='provinceEr' className='cui alert-small error'>
                        {info.province.errorMessage}
                    </div>
                )}
            </div>
        ) : (
            <LabelledInput field={info[field]} onChangeHandler={onChange} resources={resources} />
        )
    );

interface ILabelledInputProps {
    field: IFormInput;
    onChangeHandler: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
    resources: IBuyboxResources;
}
const LabelledInput: React.FC<ILabelledInputProps> = props => {
    const { label, isRequired, name, errorID, errorMessage, type, value, inputWidth } = props.field;
    return (
        <div className={inputWidth}>
            <label htmlFor={name} className='req'>
                {label}
                {isRequired && <span className='starick'>{props.resources.requiredText}</span>}
            </label>
            <input
                className={`form-field ${errorMessage && 'error'}`}
                type={type}
                name={name}
                aria-describedby={errorID}
                id={name}
                value={value}
                onChange={props.onChangeHandler}
                placeholder={props.field.placeHolder?.toString() || ''}
            />
            {errorMessage && (
                <div id={errorID} className='cui alert-small error'>
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

interface IAgentInformationSectionProps {
    resources: IBuyboxResources;
    agentInformation: ILCSABAgentInformation;
    agentInformationHandler: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
    usingAgent: IFormRadioSelection;
    usingAgentHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
const AgentInformationSection: React.FC<IAgentInformationSectionProps> = props => {
    const { resources, usingAgent, usingAgentHandler, agentInformation, agentInformationHandler } = props;
    const usingAgentOptions = ['Yes', 'No', 'Unknown'];
    return (
        <form className='form-wrap' action='agentInfo'>
            <h3 id='usingAgent'>{props.resources.lcsabAgentInfoLabel}</h3>
            <div className='option-list'>
                <strong>
                    {props.resources.lcsabOptionListLabel} <span className='starick'>{resources.requiredText}</span>
                </strong>
                <ul>
                    {usingAgentOptions.map(option => {
                        return (
                            <li>
                                <label htmlFor='agentInfo'>
                                    <input name='agentInfo' id='agentInfo' type='radio' value={option} onChange={usingAgentHandler} />
                                    {option}
                                </label>
                            </li>
                        );
                    })}
                </ul>
                {usingAgent.errorMessage && (
                    <div aria-live='assertive' aria-label={usingAgent.errorMessage} role='alert' className='cui alert-small error'>
                        {usingAgent.errorMessage}
                    </div>
                )}
            </div>
            {usingAgent.value.toLowerCase() === 'yes' && (
                <div className='form-group' id='agentInfo'>
                    {/* TODO: This section will appear only if user select Yes option */}
                    <div className='row'>
                        <div className='col-sm-6'>
                            <label htmlFor='agentName' className='req'>
                                {props.resources.sdabAgentNameLabel}
                                <span className='starick'>{resources.requiredText}</span>
                            </label>
                            <input
                                className={`form-field ${agentInformation.agentName.errorMessage && 'error'}`}
                                type='text'
                                name='agentName'
                                id='agentName'
                                aria-describedby='agentNameEr'
                                onChange={agentInformationHandler}
                                value={agentInformation.agentName.value}
                            />
                            {agentInformation.agentName.errorMessage && (
                                <div id='agentNameEr' className='cui alert-small error'>
                                    {agentInformation.agentName.errorMessage}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='row'>{renderPersonInfoInputs(agentInformation, agentInformationHandler, resources)}</div>
                </div>
            )}
        </form>
    );
};

interface IAppealTypeProps {
    resources: IBuyboxResources;
    appealType: IFormRadioSelection;
    appealTypeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
    petAppealType: IFormRadioSelection;
    petAppealTypeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
    remedialType: IFormRadioSelection;
    remedialTypeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
    licenseTypeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
    licenseType: IFormRadioSelection;
    typeOfLicense: IFormRadioSelection;
    typeOfLicenseHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    dateAndReasonForAppeal: IDateAndReasonForAppeal;
    dateAndReasonForAppealHandler: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => void;
}
export enum PetAppealType {
    ViciousAnimal = 'Vicious Animal designation order, and/or condition',
    NuisanceAnimal = 'Nuisance Animal designation order, and/or condition',
    Urban = 'Urban Livestock, or related condition'
}
export enum RemedialType {
    Property = 'Property related',
    Animal = 'Animal-related'
}
export enum LicensePermitType {
    Refused = 'Refused',
    Revoked = 'Revoked',
    Suspended = 'Suspended',
    NotRenewed = 'Not renewed',
    Conditional = 'Conditional'
}
export enum AppealType {
    PetOwnershipAppeals = 'Pet Ownership Appeals',
    LicensePermitDecisionOrNotice = 'License/permit Decision or Notice',
    RemedialOrder = 'Remedial Order'
}

const renderLicenseTypeOptions = () => {
    return ALL_LICENSE_TYPES.map(type => (
        <option key={type} value={type}>
            {type}
        </option>
    ));
};

const AppealTypeSection: React.FC<IAppealTypeProps> = props => {
    const { resources, appealType, appealTypeHandler, petAppealType, petAppealTypeHandler, remedialType, remedialTypeHandler } = props;
    const {
        licenseType,
        licenseTypeHandler,
        typeOfLicense,
        typeOfLicenseHandler,
        dateAndReasonForAppeal,
        dateAndReasonForAppealHandler
    } = props;
    const {
        appealTypeHeader,
        categoryOfAppeal,
        typeOfPetOwnershipAppeal,
        typeOfLesson,
        myLicense,
        typeOfRemedialOrderAppeal,
        reasonForAppeal,
        requiredText
    } = resources;

    return (
        <>
            <form className='form-wrap' id='appealType'>
                <h3>{appealTypeHeader}</h3>
                <div className='option-list'>
                    <strong>
                        {categoryOfAppeal} <span className='starick'>{requiredText}</span>
                    </strong>
                    <ul>
                        {Object.values(AppealType).map(appealOption => (
                            <li>
                                <label htmlFor='appealType'>
                                    <input
                                        name='appealType'
                                        id='appealType'
                                        type='radio'
                                        value={appealOption}
                                        onChange={appealTypeHandler}
                                    />
                                    {appealOption}
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
                {appealType.errorMessage && (
                    <div aria-live='assertive' aria-label={appealType.errorMessage} role='alert' className='cui alert-small error'>
                        {appealType.errorMessage}
                    </div>
                )}
            </form>
            {appealType.value === AppealType.PetOwnershipAppeals && (
                <form className='form-wrap' action='ownership'>
                    <div className='option-list'>
                        <strong>
                            {typeOfPetOwnershipAppeal} <span className='starick'>{requiredText}</span>
                        </strong>
                        <ul>
                            {Object.values(PetAppealType).map(petAppealOption => (
                                <li>
                                    <label htmlFor='petAppeal1'>
                                        <input
                                            name='petAppeal'
                                            id='petAppeal1'
                                            type='radio'
                                            value={petAppealOption}
                                            onChange={petAppealTypeHandler}
                                        />
                                        {petAppealOption}
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {petAppealType.errorMessage && (
                        <div aria-live='assertive' aria-label={petAppealType.errorMessage} role='alert' className='cui alert-small error'>
                            {petAppealType.errorMessage}
                        </div>
                    )}
                </form>
            )}
            {appealType.value === AppealType.LicensePermitDecisionOrNotice && (
                <form className='form-wrap' action='remedial' id='licenseType'>
                    <div className='option-list'>
                        <strong>
                            {typeOfLesson} <span className='starick'>{resources.requiredText}</span>
                        </strong>
                        <div className='row form-group'>
                            <div className='col-sm-6'>
                                <select
                                    className='form-field'
                                    value={typeOfLicense.value}
                                    onChange={typeOfLicenseHandler}
                                    data-live-search='true'
                                >
                                    {renderLicenseTypeOptions()}
                                </select>
                            </div>
                        </div>
                        {typeOfLicense.errorMessage && (
                            <div
                                aria-live='assertive'
                                aria-label={typeOfLicense.errorMessage}
                                role='alert'
                                className='cui alert-small error'
                            >
                                {typeOfLicense.errorMessage}
                            </div>
                        )}
                        <strong id='licensePermit'>
                            {myLicense} <span className='starick'>{resources.requiredText}</span>
                        </strong>
                        <ul>
                            {Object.values(LicensePermitType).map(licensePermitOption => (
                                <li>
                                    <label htmlFor='petAppeal2'>
                                        <input
                                            name='petAppeal'
                                            id='petAppeal2'
                                            type='radio'
                                            value={licensePermitOption}
                                            onChange={licenseTypeHandler}
                                        />
                                        {licensePermitOption}
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {licenseType.errorMessage && (
                        <div aria-live='assertive' aria-label={licenseType.errorMessage} role='alert' className='cui alert-small error'>
                            {licenseType.errorMessage}
                        </div>
                    )}
                </form>
            )}
            {appealType.value === AppealType.RemedialOrder && (
                <form className='form-wrap' action='remedial'>
                    <div className='option-list'>
                        <strong>
                            {typeOfRemedialOrderAppeal} <span className='starick'>{resources.requiredText}</span>
                        </strong>
                        <ul>
                            {Object.values(RemedialType).map(remedialOption => (
                                <li>
                                    <label htmlFor='petAppeal3'>
                                        <input
                                            name='petAppeal'
                                            id='petAppeal3'
                                            type='radio'
                                            value={remedialOption}
                                            onChange={remedialTypeHandler}
                                        />
                                        {remedialOption}
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {remedialType.errorMessage && (
                        <div aria-live='assertive' aria-label={remedialType.errorMessage} role='alert' className='cui alert-small error'>
                            {remedialType.errorMessage}
                        </div>
                    )}
                </form>
            )}
            <form className='form-wrap'>
                <div className='form-group'>
                    <label htmlFor={dateAndReasonForAppeal.date.name} id='date'>
                        {resources.lcsabDecisionDateLabel}
                        <span className='starick'>{resources.requiredText}</span>
                    </label>
                    <input
                        type='date'
                        style={{ marginBottom: '2rem' }}
                        className={`form-field ${dateAndReasonForAppeal.date.errorMessage && 'error'}`}
                        name={dateAndReasonForAppeal.date.name}
                        id={dateAndReasonForAppeal.date.name}
                        aria-describedby={'dateEr'}
                        value={dateAndReasonForAppeal.date.value}
                        onChange={dateAndReasonForAppealHandler}
                    />
                    {dateAndReasonForAppeal.date.errorMessage && (
                        <div id={'dateEr'} className='cui alert-small error'>
                            {dateAndReasonForAppeal.date.errorMessage}
                        </div>
                    )}
                </div>

                <div className='form-group'>
                    <label htmlFor={dateAndReasonForAppeal.reasonForAppeal.name} id='reasonForAppeal'>
                        {resources.sdabReasonAppealLabel}
                        <span className='starick'>{resources.requiredText}</span>
                        <small>{reasonForAppeal}</small>
                    </label>
                    <textarea
                        className={`form-field ${dateAndReasonForAppeal.reasonForAppeal.errorMessage && 'error'}`}
                        name={dateAndReasonForAppeal.reasonForAppeal.name}
                        id={dateAndReasonForAppeal.reasonForAppeal.name}
                        aria-describedby={'reasonForAppealEr'}
                        value={dateAndReasonForAppeal.reasonForAppeal.value}
                        onChange={dateAndReasonForAppealHandler}
                        maxLength={1500}
                    ></textarea>
                    {dateAndReasonForAppeal.reasonForAppeal.errorMessage && (
                        <div id={'reasonForAppealEr'} className='cui alert-small error'>
                            {dateAndReasonForAppeal.reasonForAppeal.errorMessage}
                        </div>
                    )}
                    <strong className='characterNum'>
                        <input className='form-field' type='text' value={1500 - dateAndReasonForAppeal.reasonForAppeal.value.length} />
                        {resources.sdabFormCharacterNum}
                    </strong>
                </div>
                {addAppealToCartConfirmationAcknowledgement(resources)}
            </form>
        </>
    );
};
export default LCSABForm;
